import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosInstance';
import ENDPOINTS from '../../config/apiUrls';

/**
 * Fetches users/list and returns users whose stationGroup (comma-separated) contains
 * at least one group from the comma-separated allottedStationGroups list.
 */
export const fetchAllotedStationUsers = createAsyncThunk(
  'stationGroups/fetchAllotedStationUsers',
  async (allotedStationGroups, { rejectWithValue }) => {
    if (!allotedStationGroups || typeof allotedStationGroups !== 'string') {
      return { data: [], meta: {} };
    }
    const allottedSet = new Set(
      allotedStationGroups.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
    );
    if (allottedSet.size === 0) {
      return { data: [], meta: {} };
    }

    try {
      const response = await axiosInstance.get(ENDPOINTS.USERS.LIST);
      const { data = [], meta = {} } = response.data || {};
      const collected = data.filter((user) => {
        const roles = user.attributes?.roles || [];
        if (!Array.isArray(roles) || roles.length === 0) return false;
        const stationGroupStr = user.attributes?.stationGroup || user.attributes?.metadata?.['1']?.api?.stationGroup;
        if (!stationGroupStr) return false;
        const userGroups = stationGroupStr.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
        return userGroups.some((sg) => allottedSet.has(sg));
      });
      return { data: collected, meta: { ...meta, total: collected.length } };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.errors?.[0]?.detail || err.message || 'Failed to fetch station users'
      );
    }
  }
);

const initialState = {
  users: [],
  loading: false,
  error: null,
  meta: {},
  lastFetchedAllotedStationGroups: null, // skip re-fetch when visiting Switch station again
};

const stationGroupsSlice = createSlice({
  name: 'stationGroups',
  initialState,
  reducers: {
    clearStationGroupsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllotedStationUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllotedStationUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.users = action.payload.data || [];
        state.meta = action.payload.meta || {};
        state.lastFetchedAllotedStationGroups = action.meta.arg ?? null;
      })
      .addCase(fetchAllotedStationUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch';
        state.users = [];
        state.lastFetchedAllotedStationGroups = null;
      });
  },
});

export const { clearStationGroupsError } = stationGroupsSlice.actions;
export default stationGroupsSlice.reducer;
